import { NgFor, AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatSlideToggleChange, MatSlideToggle } from '@angular/material/slide-toggle';
import { RootState } from '@next/core-lib/app';
import { SummaryCardComponent } from '@next/core-lib/components';
import { TranslatePipe } from '@next/core-lib/i18n';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';

import { take } from 'rxjs/operators';
import {
  FeedIntakeUnit,
  FeedUnit,
  TemperatureUnit,
  WaterIntakeUnit,
  WaterUnit,
  WeightUnit,
} from 'src/app/reference-value-sheet/models/reference-value-sheet.model';

import { RVSSettings, UnitOfMeasureSettings } from '../../models/setting.model';
import * as settingActions from '../../store/actions/setting.actions';
import {
  getDayOffset,
  getObservationSwitchSetting,
  getRVSSettings,
  getUnitOfMeasureSettings,
} from '../../store/selectors/setting.selector';

@Component({
  selector: 'app-performance-setting',
  templateUrl: './performance-setting.component.html',
  styleUrls: ['./performance-setting.component.scss'],
  imports: [
    SummaryCardComponent,
    MatSlideToggle,
    ReactiveFormsModule,
    FormsModule,
    MatRadioGroup,
    NgFor,
    MatRadioButton,
    MatFormField,
    MatLabel,
    MatInput,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class PerformanceSettingComponent implements OnDestroy, OnInit {
  private readonly store = inject<Store<RootState>>(Store);

  observationSwitchSetting: boolean;
  observationSwitchSubscription: Subscription;
  temperatureUnits: TemperatureUnit[];
  feedUnits: FeedUnit[];
  waterUnits: WaterUnit[];
  weightUnits: WeightUnit[];

  rvsSettings$: Observable<RVSSettings> = this.store.select(getRVSSettings);
  unitOfMeasureSettings$: Observable<UnitOfMeasureSettings> =
    this.store.select(getUnitOfMeasureSettings);
  feedIntakeUnits: FeedIntakeUnit[];
  waterIntakeUnits: WaterIntakeUnit[];

  dayOffset$: Observable<number> = this.store.select(getDayOffset);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    const store = this.store;

    this.observationSwitchSubscription = store
      .select(getObservationSwitchSetting)
      .subscribe((setting) => {
        this.observationSwitchSetting = setting;
      });
  }

  switchObservationSetting(event: MatSlideToggleChange): void {
    this.store.dispatch(
      settingActions.setObservationSwitch({ observationSwitch: event.checked, save: true }),
    );
  }

  ngOnDestroy() {
    if (this.observationSwitchSubscription !== undefined) {
      this.observationSwitchSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.temperatureUnits = Object.values(TemperatureUnit);
    this.feedUnits = Object.values(FeedUnit);
    this.waterUnits = Object.values(WaterUnit);
    this.weightUnits = Object.values(WeightUnit);
    this.feedIntakeUnits = Object.values(FeedIntakeUnit);
    this.waterIntakeUnits = Object.values(WaterIntakeUnit);
  }

  changeDayOffset(event: number) {
    this.store.dispatch(settingActions.setDayOffset({ dayOffset: event, save: true }));
  }

  changeSystem(value: string, unit: string): void {
    if (value) {
      this.rvsSettings$.pipe(take(1)).subscribe((rvsSettings) => {
        const rvsSettingsCopy = { ...rvsSettings };
        switch (unit) {
          case 'temperatureUnit':
            rvsSettingsCopy.temperatureUnit =
              TemperatureUnit[value as keyof typeof TemperatureUnit];
            break;
          case 'feedUnit':
            rvsSettingsCopy.feedUnit = FeedUnit[value as keyof typeof FeedUnit];
            break;
          case 'waterUnit':
            rvsSettingsCopy.waterUnit = WaterUnit[value as keyof typeof WaterUnit];
            break;
          case 'weightUnit':
            rvsSettingsCopy.weightUnit = WeightUnit[value as keyof typeof WeightUnit];
            break;
        }
        this.store.dispatch(settingActions.setRVSSettings({ value: rvsSettingsCopy, save: true }));
      });
    }
  }

  changeUnitOfMeasureSettings(value: string, unit: string): void {
    if (value) {
      this.unitOfMeasureSettings$.pipe(take(1)).subscribe((unitOfMeasureSettings) => {
        const unitOfMeasureSettingsCopy = { ...unitOfMeasureSettings };
        switch (unit) {
          case 'feedIntakeUnit':
            unitOfMeasureSettingsCopy.feedIntakeUnit =
              FeedIntakeUnit[value as keyof typeof FeedIntakeUnit];
            break;
          case 'waterIntakeUnit':
            unitOfMeasureSettingsCopy.waterIntakeUnit =
              WaterIntakeUnit[value as keyof typeof WaterIntakeUnit];
            break;
        }
        this.store.dispatch(
          settingActions.setUnitOfMeasureSettings({ value: unitOfMeasureSettingsCopy, save: true }),
        );
      });
    }
  }
}
